import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  finalize,
  from,
  Observable,
  startWith,
  tap,
  switchMap,
  map,
  debounceTime,
  distinctUntilChanged,
  filter,
  of,
} from 'rxjs';
import { APIService } from 'src/app/services/api.service';
import { LoggingService } from 'src/app/services/logging.service';
import Contact from './models/contact.model';
import { DataService } from '../offline/data-service';
import { environment } from 'src/environments/environment';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { StateService } from 'src/app/services/state.service';
import { FileToDelete, FileToUpload } from '../offline/databases/data-db';
import { NzMessageService } from 'ng-zorro-antd/message';
import { v4 as uuidv4 } from 'uuid';
import { AmplifyApiService } from 'src/app/services/amplify-api.service';

export interface ContactRequest {
  limit: number;
  skip: number;
  sort: string;
  sortOrder: number;
  searchText: string;
  filter: { value: string[] }[];
}
export interface ContactResponse {
  contacts: Contact[];
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class ContactsService {
  constructor(
    private readonly apiService: APIService,
    private readonly logger: LoggingService,
    private readonly dataService: DataService,
    private readonly utilitiesService: UtilitiesService,
    private readonly stateService: StateService,
    private readonly message: NzMessageService,
  ) {}

  public setContactRequestInfo(contactRequest: ContactRequest): void {
    this.contactRequestSubject.next(contactRequest);
  }

  public getContact(contactId: string | undefined): Observable<Contact | null> {
    if (!contactId) {
      this.logger.error(new Error('No contactId provided'));
      return of(null);
    }

    this.loadingSubject.next(true);

    if (this.apiService.onLine) {
      return from(this.API.get(environment.API_NAME, `/v2/contacts/${contactId}`, {})).pipe(
        tap((contact: Contact) => {
          this.dataService.populateContacts([contact]);
        }),
        finalize(() => {
          this.loadingSubject.next(false);
        }),
      );
    } else {
      return from(this.dataService.getContact(contactId)).pipe(
        finalize(() => {
          this.loadingSubject.next(false);
        }),
      );
    }
  }

  public async createContact(
    contact: Contact,
    filesToUpload: Array<FileToUpload>,
    filesToDelete: Array<FileToDelete>,
  ): Promise<Contact> {
    if (!contact) {
      throw new Error('contact mandatory');
    }
    if (!contact.guidId) {
      contact.guidId = uuidv4();
    }
    const clone = structuredClone(contact);
    delete clone._id;
    if (this.apiService.onLine) {
      return this.API.post(environment.API_NAME, '/v2/contacts/', { body: clone });
    } else {
      return this.dataService.createContact({
        body: { content: clone, filesToUpload: filesToUpload, filesToDelete: filesToDelete },
      });
    }
  }

  public async updateContact(
    contact: Contact,
    filesToUpload: Array<FileToUpload>,
    filesToDelete: Array<FileToDelete>,
  ): Promise<Contact> {
    if (!contact) {
      throw new Error('contact mandatory');
    }

    return this.apiService.updateOnlineOfflineContact(contact, filesToUpload, filesToDelete);
  }

  public disable(contactId: string | undefined): Observable<Contact> {
    if (!contactId) {
      throw new Error('contactId mandatory');
    }

    if (this.apiService.onLine) {
      return of(null).pipe(
        tap(() => this.disableSubject.next(true)),
        switchMap(() => this.API.patch(environment.API_NAME, `/v2/contacts/disable/${contactId}`, {})),
        tap(() => this.disableSubject.next(false)),
        finalize(() => {
          this.disableSubject.next(false);
        }),
      );
    } else {
      return from(this.dataService.disableContact(contactId)).pipe(
        finalize(() => {
          this.setContactRequestInfo({
            searchText: '',
            limit: 50,
            skip: 0,
            sort: 'lastName',
            sortOrder: 1,
            filter: [{ value: [] }],
          });
          this.loadingSubject.next(false);
        }),
      );
    }
  }

  public deleteContact(contactId: string | undefined): Observable<void> {
    if (!contactId) {
      throw new Error('contactId mandatory');
    }

    this.loadingSubject.next(true);

    if (this.apiService.onLine) {
      return from(this.apiService.deleteOnlineOfflineContact(contactId)).pipe(
        tap((deleted) => {
          if (!deleted) {
            this.message.warning($localize`Contact cannot be deleted!`);
          }
        }),
        finalize(() => {
          this.setContactRequestInfo({
            searchText: '',
            limit: 50,
            skip: 0,
            sort: 'lastName',
            sortOrder: 1,
            filter: [{ value: [] }],
          });
          this.loadingSubject.next(false);
        }),
      );
    } else {
      return from(this.dataService.archiveContact(contactId)).pipe(
        finalize(() => {
          this.setContactRequestInfo({
            searchText: '',
            limit: 50,
            skip: 0,
            sort: 'lastName',
            sortOrder: 1,
            filter: [{ value: [] }],
          });
          this.loadingSubject.next(false);
        }),
      );
    }
  }

  public setCurrentContact(email: string): Observable<void> {
    if (!email) {
      throw new Error('email mandatory to setCurrentContact');
    }
    return from(this.getContactByEmail(email)).pipe(
      distinctUntilChanged(),
      map((contact) => this.currentContactSubject.next(contact)),
    );
  }

  public async listContactsInGroup(groupName: string): Promise<
    {
      firstName: string;
      lastName: string;
      socialReason: string;
      email: string;
      identificationNumber: string;
      taxIdNumber: string;
    }[]
  > {
    return this.API.get(environment.API_NAME, `/v2/groups/${groupName}/contacts`, {});
  }

  public clearTooManyResult(): void {
    this.tooManyResultSubject.next(false);
  }

  public getContactByUsername(username: string): Observable<Contact> {
    const filters = [{ field: 'cognitoUser.Username', value: username, operator: 'eq' }];

    if (this.apiService.onLine) {
      return from(this.apiService.getCollection({ collection: 'contacts', filters, limit: 1 })).pipe(
        map((contacts) => contacts[0]),
      );
    } else {
      return from(this.dataService.getContactsByFieldValue('cognitoUser.Username', username, 0, 100)).pipe(
        map((contacts) => contacts[0]),
      );
    }
  }

  private getContactByEmail(email: string): Observable<Contact> {
    const filters = [{ field: 'email', value: email, operator: 'eq' }];
    if (this.apiService.onLine) {
      return from(this.apiService.getCollection({ collection: 'contacts', filters, limit: 1 })).pipe(
        tap((contacts: Contact[]) => {
          this.dataService.populateContacts(contacts);
        }),
        map((contacts) => contacts[0]),
      );
    } else {
      return from(this.dataService.getContactsByFieldValue('email', email, 0, 100)).pipe(
        map((contacts) => contacts[0]),
      );
    }
  }

  private fetchContacts(contactRequest: ContactRequest | null): Observable<Contact[]> {
    if (!contactRequest) {
      return of([]);
    }

    this.loadingSubject.next(true);
    if (this.apiService.onLine) {
      if (!contactRequest.searchText) {
        const filters: any[] = [];
        if (contactRequest?.filter[0]?.value?.length) {
          filters.push({
            field: 'type',
            value: contactRequest?.filter[0]?.value,
            operator: 'in',
          });
        }

        return from(
          this.API.get(
            environment.API_NAME,
            `/v2/contacts?filters=${encodeURIComponent(JSON.stringify(filters))}&limit=${contactRequest.limit}&skip=${
              contactRequest.skip
            }&sort=${contactRequest.sort}&sortOrder=${contactRequest.sortOrder}&language=${
              this.utilitiesService.locale.locale
            }`,
            {},
          ),
        ).pipe(
          tap((contactResponse: ContactResponse) => {
            this.dataService.populateContacts(contactResponse.contacts);
            this.contactsCountSubject.next(contactResponse.count);
            this.tooManyResultSubject.next(false);
          }),
          map((contactResponse: ContactResponse) => {
            return contactResponse.contacts;
          }),
          switchMap((contacts) =>
            from(this.stateService.getDomainValue('contactType')).pipe(
              map((domainValues) => {
                contacts.forEach((contact) => {
                  contact.typeUI = contact.type.map((x) => {
                    const type = domainValues.find((domainValue) => domainValue.value === x);
                    return this.utilitiesService.getLocalizedLabel(type, 'label');
                  });
                });
                return contacts;
              }),
            ),
          ),
          finalize(() => this.loadingSubject.next(false)),
        );
      } else {
        return from(this.apiService.searchContacts(contactRequest.searchText)).pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap((contacts) => {
            if (contacts.length <= contactRequest.limit) {
              this.contactsCountSubject.next(contacts.length);
              this.tooManyResultSubject.next(false);
            } else {
              this.contactsCountSubject.next(contactRequest.limit);
              this.tooManyResultSubject.next(true);
            }
          }),
          map((contacts) => {
            return contacts.slice(0, contactRequest.limit);
          }),
          switchMap((contacts) =>
            from(this.stateService.getDomainValue('contactType')).pipe(
              map((domainValues) => {
                contacts.forEach((contact) => {
                  contact.typeUI = contact.type.map((x) => {
                    const type = domainValues.find((domainValue) => domainValue.value === x);
                    return this.utilitiesService.getLocalizedLabel(type, 'label');
                  });
                });
                return contacts;
              }),
            ),
          ),
          finalize(() => this.loadingSubject.next(false)),
        );
      }
    } else {
      return from(this.dataService.getContacts(contactRequest.skip, contactRequest.limit)).pipe(
        tap((contacts) => {
          this.contactsCountSubject.next(contacts.length);
        }),
        switchMap((contacts) =>
          from(this.stateService.getDomainValue('contactType')).pipe(
            map((domainValues) => {
              contacts.forEach((contact) => {
                contact.typeUI = contact.type.map((x: any) => {
                  const type = domainValues.find((domainValue) => domainValue.value === x);
                  return this.utilitiesService.getLocalizedLabel(type, 'label');
                });
              });
              return contacts;
            }),
          ),
        ),
        finalize(() => this.loadingSubject.next(false)),
      );
    }
  }

  public resetContactList(): void {
    this.ancienRequest = '';
  }

  private readonly API = inject(AmplifyApiService);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly disableSubject = new BehaviorSubject<boolean>(false);
  private readonly contactRequestSubject = new BehaviorSubject<ContactRequest | null>(null);
  private readonly contactsCountSubject = new BehaviorSubject<number>(0);
  private readonly tooManyResultSubject = new BehaviorSubject<boolean>(false);
  private readonly currentContactSubject = new BehaviorSubject<Contact | null>(null);
  private ancienRequest = '';

  public loading$ = this.loadingSubject.asObservable();
  public disabling$ = this.disableSubject.asObservable();
  public contactsCount$ = this.contactsCountSubject.asObservable();
  public tooManyResult$ = this.tooManyResultSubject.asObservable();
  public filteredContacts$ = this.contactRequestSubject.asObservable().pipe(
    filter((contactRequest) => !!contactRequest && JSON.stringify(contactRequest) !== this.ancienRequest),
    tap((contactRequest) => (this.ancienRequest = JSON.stringify(contactRequest))),
    switchMap((contactRequest) => this.fetchContacts(contactRequest)),
    startWith([]),
  );
  public currentContact$ = this.currentContactSubject.asObservable().pipe(filter((contact) => !!contact));
}
